<script setup>
import { computed, ref, watch } from 'vue';
import FloatingSelect from '@/components/FloatingSelect.vue';
import listIcon from '@/assets/imgs/icons/list-solid.svg';

const emit = defineEmits(['select', 'update:modelValue']);
const props = defineProps({
  options: { type: Array, default: () => [] },
  triggerLabel: { type: String, default: 'Produkt wählen…' },
  placeholder: { type: String, default: 'Produkt wählen…' },
  modelValue: { type: [String, Number, null], default: null },
  optionKey: { type: String, default: 'id' },
  optionLabel: { type: [String, Function], default: 'name' },
});

function normId(v){
  if (v === null || v === undefined) return -1;
  if (typeof v === 'number') return v;
  const s = String(v);
  const m = s.match(/(\d+)$/);
  return m ? parseInt(m[1], 10) : -1;
}

const safeOptions = computed(() => Array.isArray(props.options)
  ? props.options
      .map(p => ({ id: normId(p?.id), name: p?.name || '' }))
      // Filter out invalid/sentinel entries and empty names
      .filter(p => p.id !== undefined && p.name && String(p.id) !== '-1' && p.id !== -1)
  : []
);

const visibleItems = computed(() => Math.max(1, Math.min(6, safeOptions.value.length || 1)));
const itemHeight = 40;
const maxVisibleItems = computed(() => visibleItems.value);

function onSelect(opt){ emit('select', opt || null); }

// two-way binding for current selection (id)
const internalVal = ref(props.modelValue);
watch(() => props.modelValue, (nv) => { internalVal.value = nv; });
function updateValue(v){
  const nv = normId(v);
  internalVal.value = nv;
  emit('update:modelValue', nv);
  // Also emit select with corresponding object for convenience
  const found = safeOptions.value.find(o => String(o[props.optionKey]) === String(nv)) || null;
  emit('select', found);
}

const currentLabel = computed(() => {
  const found = safeOptions.value.find(o => String(o[props.optionKey]) === String(normId(internalVal.value)));
  if (!found) return '';
  if (typeof props.optionLabel === 'function') return props.optionLabel(found);
  return found?.[props.optionLabel] ?? '';
});

const lastQuery = ref('');
const loading = ref(false);
let searchTimer = null;
function handleSearch(q){
  lastQuery.value = q?.trim();
  loading.value = true;
  clearTimeout(searchTimer);
  searchTimer = setTimeout(()=> { loading.value = false; }, 300);
}
</script>

<template>
  <FloatingSelect
    size="sm"
    :options="safeOptions"
    :model-value="internalVal"
    :option-key="optionKey"
    :option-label="optionLabel"
    :search-prop="['name']"
    :item-height="itemHeight"
    :max-visible-items="maxVisibleItems"
    :popover-margin="10"
    :match-trigger-width="true"
    :placeholder="placeholder"
    :return-object="false"
    aria-label="Produkt auswählen"
    @update:modelValue="updateValue"
    @select="onSelect"
    @search="handleSearch"
  >
    <template #before-list>
      <div v-if="loading" class="fs-inline-loading" role="status" aria-live="polite">
        <span class="spinner" aria-hidden="true"></span>
        <span class="sr-only">Suche läuft...</span>
      </div>
    </template>
    <template #trigger="{ open }">
      <button type="button" class="btn btn--pill btn--sm btn--outline pd-trigger" :aria-expanded="open.toString()">
        <span class="icon-wrapper" aria-hidden="true"><img :src="listIcon" alt="" class="list-icon" /></span>
        <span class="label" v-if="currentLabel">{{ currentLabel }}</span>
        <span class="label prefix" v-else-if="props.triggerLabel">{{ props.triggerLabel }}</span>
        <span class="label" v-else-if="placeholder">{{ placeholder }}</span>
        <svg class="chevron" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path fill="currentColor" d="M7 10l5 5 5-5z"/></svg>
      </button>
    </template>
    <template #item="{ option, highlighted }">
      <div class="pd-item" :class="{ 'is-highlighted': highlighted }">
        <div class="pd-meta">
          <div class="pd-name">{{ option?.name || '' }}</div>
        </div>
      </div>
    </template>
    <template #empty>
      <div class="fs-empty-custom" role="status" aria-live="polite">
        <template v-if="loading">
          <span class="spinner small" aria-hidden="true"></span>
          <span>Suche...</span>
        </template>
        <template v-else-if="lastQuery">
          <span class="error-msg">Keine Treffer für "{{ lastQuery }}"</span>
        </template>
        <template v-else>
          Keine Einträge gefunden
        </template>
      </div>
    </template>
  </FloatingSelect>
</template>

<style scoped>
.pd-trigger { display:flex; align-items:center; gap:.4rem; padding:.45rem .7rem; width:100%; }
.pd-trigger::before {display: none;}
.pd-trigger .chevron { position:absolute; right:12px; top:50%; transform:translateY(-50%); color:var(--btn-text-muted, #94a3b8); pointer-events:none; }
.pd-trigger .icon-wrapper { display:flex; align-items:center; justify-content:center; width:20px; height:20px; border-radius:6px; background:var(--btn-bg-soft, rgba(255,255,255,0.06)); box-shadow:0 0 0 1px rgba(255,255,255,0.05) inset; }
.theme--light .pd-trigger .icon-wrapper { background:#f1f5f9; box-shadow:0 0 0 1px #d9e2ec inset; }
.pd-trigger .list-icon { width:13px; height:13px; display:block; opacity:.78; filter:brightness(1.05); }
.theme--light .pd-trigger .list-icon { filter:none; opacity:.72; }
.pd-trigger .label { font-weight:600; letter-spacing:.35px; }
.pd-trigger .label.prefix { opacity:.7; margin-right:.35rem; }
.pd-item { display:flex; align-items:center; gap:.55rem; text-align:left; }
.pd-name { font-weight:600; letter-spacing:.4px; font-size:.66rem; }
.fs-inline-loading { display:flex; align-items:center; gap:.5rem; font-size:.58rem; padding:.15rem .35rem .3rem; color:#c8d0d6; }
.theme--light .fs-inline-loading { color:#334654; }
.spinner { width:16px; height:16px; border:2px solid rgba(255,255,255,0.35); border-top-color:var(--sellwerk-color-secondary, #16a34a); border-radius:50%; animation:spin .6s linear infinite; }
.spinner.small { width:14px; height:14px; border-width:2px; }
.theme--light .spinner { border-color:rgba(0,0,0,0.2); border-top-color:var(--sellwerk-color-main,#0f766e); }
@keyframes spin { to { transform:rotate(360deg); } }
.fs-empty-custom { display:flex; align-items:center; gap:.6rem; font-size:.6rem; padding:.55rem .4rem .65rem; opacity:.9; }
.fs-empty-custom .error-msg { color:#f87171; font-weight:600; letter-spacing:.3px; }
.theme--light .fs-empty-custom .error-msg { color:#dc2626; }
.sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0 0 0 0); white-space:nowrap; border:0; }
</style>
